import { useMemo } from 'react';

import { ContextFormFieldNumber } from '@bambi/component-library/src/components/forms/context/ContextFormFieldNumber';
import { useFormContext } from 'react-hook-form';

import { useOrganizationSettingsRetrieveQuery } from 'src/common/external/bambi-api/bambiApi';
import { MultiSelectContextField } from 'src/common/FormField/MultiSelectContextField';

import { SettingsRowHeader } from './components/SettingsRowHeader';

const DAYS_OF_ASSIGNMENTS_MIN = 1;
const DAYS_OF_ASSIGNMENTS_MAX = 30;
const DAYS_OF_TRIPS_MIN = 1;
const DAYS_OF_TRIPS_MAX = 30;

export function MobileAppSettings() {
  const { data: orgSettings } = useOrganizationSettingsRetrieveQuery({});
  const { getValues } = useFormContext();

  const associateCancellationReasons = useMemo(() => {
    const reasons = orgSettings?.bambi_cancel_reasons || [];
    return reasons.map((reason) => ({
      label: reason.label,
      value: reason.value || '',
    }));
  }, [orgSettings]);

  return (
    <div className="grid grid-cols-1 gap-x-4 gap-y-4 border-b border-gray-900/10 py-5 md:grid-cols-4">
      <SettingsRowHeader
        title="Mobile App Settings"
        description="Organization defaults for the driver mobile app."
      />

      <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-4 md:col-span-3">
        <div className="flex flex-col gap-y-4">
          <ContextFormFieldNumber
            // Not a typo: This is a maximum from the user's perspective
            label="Maximum Upcoming Trips Shown"
            fieldPath="minimum_upcoming_trips_returned"
            placeholder="Maximum trips"
            helperText={{
              text: maximumUpcomingTripsHelperText,
              presentation: {
                type: 'tooltip',
              },
            }}
          />
          <ContextFormFieldNumber
            label="Days of Future Schedules Shown"
            fieldPath="days_of_future_assignments_to_show"
            placeholder="Number of days"
            rules={{
              validate: (value) => {
                const assignmentsValue = parseInt(value);
                if (
                  assignmentsValue < DAYS_OF_ASSIGNMENTS_MIN ||
                  assignmentsValue > DAYS_OF_ASSIGNMENTS_MAX
                ) {
                  return `Number of days must be between ${DAYS_OF_ASSIGNMENTS_MIN} and ${DAYS_OF_ASSIGNMENTS_MAX}`;
                }
                const tripsValue = getValues('days_of_future_trips_to_show');
                if (assignmentsValue < parseInt(tripsValue)) {
                  return 'Value must be greater than or equal to the "Days of Future Trips Shown" setting';
                }
                return true;
              },
            }}
            helperText={{
              text: daysOfFutureAssignmentsToShowHelperText,
              presentation: {
                type: 'tooltip',
              },
            }}
          />
          <ContextFormFieldNumber
            label="Days of Future Trips Shown"
            fieldPath="days_of_future_trips_to_show"
            placeholder="Number of days"
            rules={{
              validate: (value) => {
                const tripsValue = parseInt(value);
                if (
                  tripsValue < DAYS_OF_TRIPS_MIN ||
                  tripsValue > DAYS_OF_TRIPS_MAX
                ) {
                  return `Number of days must be between ${DAYS_OF_TRIPS_MIN} and ${DAYS_OF_TRIPS_MAX}`;
                }
                const assignmentsValue = getValues(
                  'days_of_future_assignments_to_show'
                );
                if (tripsValue > parseInt(assignmentsValue)) {
                  return 'Value must be less than or equal to the "Days of Future Schedules Shown" setting';
                }
                return true;
              },
            }}
            helperText={{
              text: daysOfFutureTripsToShowHelperText,
              presentation: {
                type: 'tooltip',
              },
            }}
          />
        </div>
        <div className="col-span-1"></div>
        <div className="col-span-2">
          <MultiSelectContextField
            fieldPath="associate_cancel_reasons"
            label="Associate Cancellation Reasons"
            placeholder="Select reasons"
            rules={{
              validate(value) {
                return value.length >= 1 || 'Select at least one reason';
              },
            }}
            options={associateCancellationReasons}
            helperText={{
              text: associateCancellationReasonsHelperText,
              presentation: {
                type: 'tooltip',
              },
            }}
          />
        </div>
      </div>
    </div>
  );
}

const associateCancellationReasonsHelperText = `
  Trip brokers define their own list of allowable cancellation reasons. For
  all other trips, you can limit which reasons your associates can see and
  select. Roles higher than associate will still have access to all of
  these reasons in the mobile and web apps. Also see the setting:
  "Allow drivers to cancel trips".
`;

const maximumUpcomingTripsHelperText = `
  You can adjust this number depending on how many trips
  you want to show to drivers on the Active tab. To start, we take the
  current active trip or first upcoming trip, and include any trips that
  overlap with it. If this setting is higher than that number, we will
  return additional trips until the setting is met, or there are no more
  trips assigned to the driver.
`;

const daysOfFutureAssignmentsToShowHelperText = `
  This determines how many days of future crew schedules drivers can see under Schedule > Shifts.
  The number is in addition to the current day. So if today is Monday and the setting is 2,
  drivers will be able to see schedules for Monday, Tuesday, and Wednesday. This should be 
  greater than or equal to the "Days of Future Trips Shown" setting.
`;

const daysOfFutureTripsToShowHelperText = `
  This determines how many days of future trips drivers can see under Schedule > Shifts > Trips.
  The number is in addition to the current day. So if today is Monday and the setting is 2,
  drivers will be able to see trips for Monday, Tuesday, and Wednesday. This should be 
  less than or equal to the "Days of Future Schedules Shown" setting.
`;
